import {
  useUpdateMultipleAttributeValuesAndAddAttributeValueGroupsMutation,
  AttributeValues_Insert_Input,
  AttributeValueGroups_Insert_Input,
  AttributeValueUploads_Insert_Input,
  AttributeValueUploads_Arr_Rel_Insert_Input,
  AttributeValueUploadVersions_Insert_Input,
  AttributeValueUploadVersions_Bool_Exp,
  AttributeFiles,
} from '@organice/graphql'
import {
  Service,
  ServiceModule,
  AttributeValue,
  Attribute,
  AttributeGroup,
  AttributeType,
  AttributeValueGroup,
} from '../../../types/service'
import { prepareDate } from '@organice/utils/service/prepareDate'
import { useTranslation } from 'react-i18next'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FormData = Record<string, any>
export type FormDataValue = FormData[keyof FormData]
/* existing attributeValueGroups removed in the UI, by serviceModule id; deleted on save */
export type RemovedAttributeValueGroups = Record<
  ServiceModule['id'],
  AttributeValueGroup['id'][]
>

export const SEPERATOR = '::'
export const NEW_GROUP_INDICATOR = 'newAttributeValueGroups'

type ProcessingAttribute = Pick<Attribute, 'id' | 'config' | 'attributeType'>

type NewlyUploadedFile = {
  uid: string
  name: string
  lastModified: number
  status: string
  size: number
  type: string
  response: {
    size: number
    uid: bigint
    fileId: string // !!
  }
}
type ExistingFile = {
  uid: bigint | string
  uploadId?: bigint | string
  name?: string
  url?: string
  size: number
  type?: AttributeFiles['__typename']
  versionFileIds?: (bigint | string)[]
  newVersionFileIds?: (bigint | string)[]
  deletedVersions?: { uploadId: bigint | string; fileId: bigint | string }[]
  isNewVersion?: boolean
}

type AttributeValueUploadChange = {
  attributeValueId: bigint | number
  fileId: bigint | number
  uploadId?: bigint | number | string
  name?: string
  versionFileIds?: (bigint | number | string)[]
  newVersionFileIds?: (bigint | number | string)[]
  deletedVersions?: { uploadId: bigint | string; fileId: bigint | string }[]
  isNewVersion?: boolean
}

type FileListItem = ExistingFile | NewlyUploadedFile

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useUpload = () => {
  const { t } = useTranslation()

  const [
    updateMultipleAttributeValuesAndAddAttributeValueGroups,
    {
      loading: updateValuesAndCreateValueGroupsLoading,
      error: updateValuesAndCreateValueGroupsError,
    },
  ] = useUpdateMultipleAttributeValuesAndAddAttributeValueGroupsMutation()

  const upload = async (
    data: FormData,
    service: Service,
    removedAttributeValueGroups: RemovedAttributeValueGroups = {}
  ) => {
    // console.log(
    //   `handling data`,
    //   { data, entries: Object.entries(data) },
    //   service
    // )

    // a module with removed valueGroups only has no entry in data
    const moduleIds = [
      ...Object.keys(data),
      ...Object.keys(removedAttributeValueGroups).filter(
        key =>
          !(key in data) && removedAttributeValueGroups[Number(key)]?.length
      ),
    ]

    const promises = moduleIds.map(key => {
      const module = service.serviceModules.find(m => m.id === Number(key))

      if (!module) {
        console.error(`couldn't find serviceModule ${key} in`, service)
        throw new Error(
          t('events.serviceModules.errors.serviceModules.unknownModule')
        )
      }
      return handleModule(
        { [key]: data[key] ?? {} },
        module,
        removedAttributeValueGroups[module.id] ?? []
      )
    })
    return await Promise.all(promises)
  }

  const handleModule = async (
    data: FormData,
    serviceModule: ServiceModule,
    attributeValueGroupsToDelete: AttributeValueGroup['id'][]
  ) => {
    // console.log(`handling module`, { data })

    const moduleData = Object.values(data).reduce<FormData>(
      (acc, curr: any) => ({ ...acc, ...curr }),
      {}
    )

    const entries = Object.entries(moduleData)

    /*
    /* 1) find all file attributes for attributeValueUploads mutation
    /* 2) generate all mutation data for new attributeValueGourps (if any)
    /* 3) generate all mutation data for attributeValue updates (if any)
    /*
    /* All mutations should be in a single transaction and succeed or fail together
    */

    const fileChangesByAttributeValueId = getFilesForAttributeValueIds(entries)

    const attributeValueUploadsUpdate = splitAttributeValueUploads(
      serviceModule,
      fileChangesByAttributeValueId
    )

    console.log('entries', entries, data)

    /* New attributeValueGroups if any */
    const newAttributeValueGroups: AttributeValueGroups_Insert_Input[] =
      prepareAttributeValueGroupMutationData(serviceModule, entries)

    /* Aggregate all attributeValue Updates for batch update of existing attributeValues */
    const attrValueUpdates: AttributeValues_Insert_Input[] =
      prepareAttributeValueUpdateMutationData(serviceModule, entries)

    const hasValueUpdates = !!attrValueUpdates.length
    const hasNewAttributeValueGroups = !!newAttributeValueGroups.length

    //Decide which Mutation to use (value updates & new value groups / new valueGroups only / value updates only)
    // if (hasValueUpdates && hasNewAttributeValueGroups) {
    const updateMutation =
      await updateMultipleAttributeValuesAndAddAttributeValueGroups({
        variables: {
          attributeValues: attrValueUpdates,
          attributeValueGroups: newAttributeValueGroups,
          attributeValueUploadsToDelete: attributeValueUploadsUpdate.delete,
          attributeValueUploads: attributeValueUploadsUpdate.insertUploads,
          attributeValueUploadVersions:
            attributeValueUploadsUpdate.insertVersions,
          attributeValueUploadVersionsToDelete:
            attributeValueUploadsUpdate.deleteVersions,
          attributeValueGroupsToDelete,
        },
      })

    const affectedRowsDeletedAttrValGroups =
      updateMutation.data?.delete_attributeValueGroups?.affected_rows || 0

    if (
      affectedRowsDeletedAttrValGroups !== attributeValueGroupsToDelete.length
    ) {
      throw new Error(
        t('events.serviceModules.errors.attributeValueGroups.deleteError')
      )
    }

    const affectedRowsAttrValGroups =
      updateMutation.data?.insert_attributeValueGroups?.affected_rows || 0

    const affectedRowsAttrValues =
      updateMutation.data?.insert_attributeValues?.affected_rows || 0

    checkForUpdateErrors(
      UpdateType.AttributeValueGroups,
      affectedRowsAttrValGroups,
      newAttributeValueGroups.length
    )
    checkForUpdateErrors(
      UpdateType.AtrributeValues,
      affectedRowsAttrValues,
      attrValueUpdates.length
    )
    return updateMutation
  }

  enum UpdateType {
    AtrributeValues = 'attributeValues',
    AttributeValueGroups = 'attributeValueGroups',
  }

  type FileAttributeValueChanges = {
    [key: number]: AttributeValueUploadChange[]
  }

  function splitAttributeValueUploads(
    serviceModule: ServiceModule,
    filesByAttributeValueId: FileAttributeValueChanges
  ) {
    const oldUploadsByAttributeValueId: {
      [key: number]: AttributeValueUploadChange[]
    } = {}
    const idsOfChangedUploadFields = Object.keys(filesByAttributeValueId).map(
      key => parseInt(key)
    )
    const update: {
      insertUploads: AttributeValueUploads_Insert_Input[]
      insertVersions: AttributeValueUploadVersions_Insert_Input[]
      deleteVersions: AttributeValueUploadVersions_Bool_Exp[]
      delete: bigint[]
    } = {
      insertUploads: [],
      insertVersions: [],
      deleteVersions: [],
      delete: [],
    }

    // grab old values of attributeValueUploads
    serviceModule.attributeGroups.forEach(aGroup => {
      aGroup.attributeValueGroups.forEach(avGroup => {
        avGroup.attributeValues
          .filter(
            av =>
              av.attribute.attributeType === 'file' &&
              idsOfChangedUploadFields.includes(av.id)
          )
          .forEach(fileAttr => {
            const uploads = fileAttr.attributeValueUploads.map(upload => ({
              attributeValueId: fileAttr.id as bigint,
              uploadId: upload.id as bigint,
              fileId: upload.attributeValueUploadVersions[0]?.file.id as bigint,
              versionFileIds: upload.attributeValueUploadVersions.map(
                version => version.file.id as bigint
              ),
              name: upload.name,
            }))

            oldUploadsByAttributeValueId[fileAttr.id] = uploads
          })
      })
    })

    idsOfChangedUploadFields.forEach(attributeValueId => {
      const newUploads = filesByAttributeValueId[attributeValueId] || []
      const oldUploads = oldUploadsByAttributeValueId[attributeValueId] || []
      const retainedOldUploadIds = new Set<string>()

      newUploads.forEach(newUpload => {
        newUpload.deletedVersions?.forEach(version => {
          update.deleteVersions.push({
            attributeValueUploadId: { _eq: Number(version.uploadId) },
            fileId: { _eq: Number(version.fileId) },
          })
        })

        if (
          !newUpload.uploadId &&
          newUpload.isNewVersion &&
          oldUploads.length === 1 &&
          newUpload.fileId
        ) {
          const [oldUpload] = oldUploads
          update.insertVersions.push({
            attributeValueUploadId: Number(oldUpload.uploadId),
            fileId: Number(newUpload.fileId),
          })
          retainedOldUploadIds.add(String(oldUpload.uploadId))
          return
        }

        if (newUpload.uploadId) {
          const oldUpload = oldUploads.find(
            upload => String(upload.uploadId) === String(newUpload.uploadId)
          )

          if (newUpload.newVersionFileIds?.length) {
            newUpload.newVersionFileIds.forEach(fileId => {
              if (
                !oldUpload?.versionFileIds?.some(
                  oldFileId => String(oldFileId) === String(fileId)
                )
              ) {
                update.insertVersions.push({
                  attributeValueUploadId: Number(newUpload.uploadId),
                  fileId: Number(fileId),
                })
              }
            })
            return
          }

          if (
            oldUpload &&
            String(oldUpload.fileId) !== String(newUpload.fileId) &&
            !oldUpload.versionFileIds?.some(
              fileId => String(fileId) === String(newUpload.fileId)
            )
          ) {
            update.insertVersions.push({
              attributeValueUploadId: Number(newUpload.uploadId),
              fileId: Number(newUpload.fileId),
            })
          }
          return
        }

        update.insertUploads.push({
          attributeValueId,
          name: newUpload.name,
          attributeValueUploadVersions: {
            data: [{ fileId: Number(newUpload.fileId) }],
          },
        })
      })

      oldUploads.forEach(oldUpload => {
        const stillExists = newUploads.some(
          newUpload => String(newUpload.uploadId) === String(oldUpload.uploadId)
        )
        if (
          !stillExists &&
          oldUpload.uploadId &&
          !retainedOldUploadIds.has(String(oldUpload.uploadId))
        ) {
          update.delete.push(Number(oldUpload.uploadId) as unknown as bigint)
        }
      })
    })

    return update
  }

  const checkForUpdateErrors = (
    type: UpdateType,
    affectedRows: number,
    updatesCount: number
  ) => {
    if (affectedRows !== updatesCount) {
      if (affectedRows === 0) {
        throw new Error(
          t(`events.serviceModules.errors.${type}.totalUpdateFail`)
        )
      }

      return true

      // ignoring for now, because the update of new valueGroups with files returns more affected_rows (newgroup + files)
      // leading to error like -3 of 1 updates failed
      throw new Error(
        t(`events.serviceModules.errors.${type}.partialUpdateFail`, {
          failedUpdatesCount: updatesCount - affectedRows,
          totalUpdatesCount: updatesCount,
        })
      )
    }
  }

  const getFilesForAttributeValueIds = (entries: [string, any][]) => {
    const changedFileAttributeValues: FileAttributeValueChanges = {}

    entries.forEach(e => {
      const [attrValueId, value] = e
      // New attributeValue Groups will be ignored here (fileList is a level deeper) & will be added with the new valueGroup relational insert
      if (!value?.fileList) return

      // anything that is not an attributeValue id would end up as attributeValueId: NaN in the mutation
      if (!Number.isInteger(Number(attrValueId))) {
        console.warn(
          `getFilesForAttributeValueIds(): ignoring non numeric key ${attrValueId}`
        )
        return
      }

      changedFileAttributeValues[Number(attrValueId)] = value.fileList
        .map((f: FileListItem) => {
          if (f?.type === 'attributeFile') return

          if (
            'status' in f &&
            typeof f.uid === 'string' &&
            !(f as ExistingFile).uploadId
          ) {
            if (f.status !== 'done') {
              throw new Error(
                t(`events.serviceModules.errors.uploadStillInProgress`, {
                  fileName: f.name,
                })
              )
            }
            return {
              attributeValueId: Number(attrValueId),
              fileId: Number(f.response.fileId),
              name: f.name,
            }
          }
          return {
            attributeValueId: Number(attrValueId),
            fileId: Number(f.uid),
            uploadId: (f as ExistingFile).uploadId,
            name: f.name,
            versionFileIds: (f as ExistingFile).versionFileIds,
            newVersionFileIds: (f as ExistingFile).newVersionFileIds,
            deletedVersions: (f as ExistingFile).deletedVersions,
            isNewVersion: (f as ExistingFile).isNewVersion,
          }
        })
        .filter(Boolean)
    })

    return changedFileAttributeValues
  }

  const prepareAttributeValueGroupMutationData = (
    serviceModule: ServiceModule,
    entries: [string, any][]
  ) => {
    const newAttributeValueGroupsData: AttributeValueGroups_Insert_Input[] = []

    entries.forEach(([key, value]) => {
      if (isNewAttributeValueGroup(key)) {
        const splittedKey = key.split(SEPERATOR)
        const groupId = Number([...splittedKey].pop())

        const group = serviceModule.attributeGroups.find(g => g.id === groupId)

        if (!group) {
          console.error(
            `couldn't find attributeGroup ${groupId} in`,
            serviceModule
          )
          throw new Error(
            t('events.serviceModules.errors.attributeValueGroups.unknownGroup')
          )
        }
        // a module can contain more than one repeatable attributeGroup, so collect them all
        newAttributeValueGroupsData.push(
          ...handleNewValueGroups(value as FormData[], group)
        )
      }
    })

    return newAttributeValueGroupsData
  }

  const prepareAttributeValueUpdateMutationData = (
    serviceModule: ServiceModule,
    entries: [string, any][]
  ) => {
    const attrValueUpdates: AttributeValues_Insert_Input[] = []
    entries.forEach(([key, value]) => {
      if (isNewAttributeValueGroup(key)) {
        return
      }
      const attributeValue = serviceModule.attributeGroups.reduce<
        AttributeValue | undefined
      >((acc, curr) => {
        if (acc) {
          return acc
        }
        return curr.attributeValueGroups.reduce<AttributeValue | undefined>(
          (value, avg) => {
            if (value) {
              return value
            }

            return avg.attributeValues.find(a => a.id === Number(key))
          },
          undefined
        )
      }, undefined)

      if (!attributeValue) {
        console.error(`couldn't find attributeValue ${key} in`, serviceModule)
        throw new Error(
          t('events.serviceModules.errors.attributeValues.unknownValue')
        )
      }

      if (attributeValue.attribute.attributeType === AttributeType.File) {
        return
      }

      attrValueUpdates.push(
        prepareExistingAttributeValueUpdate({ [key]: value }, attributeValue)
      )
    })

    return attrValueUpdates
  }

  const prepareExistingAttributeValueUpdate = (
    data: Record<string, unknown>,
    attributeValue: AttributeValue
  ) => {
    const [rawValue] = Object.values(data)
    const preparedValue = prepareValue(rawValue, attributeValue.attribute)

    return {
      attributeId: attributeValue.attribute.id,
      attributeValueGroupId: attributeValue.attributeValueGroupId,
      ...preparedValue,
    }
  }

  const prepareValue = (
    rawValue: FormDataValue,
    attribute: ProcessingAttribute
  ):
    | AttributeValues_Insert_Input
    | AttributeValueUploads_Arr_Rel_Insert_Input => {
    switch (attribute.attributeType) {
      case AttributeType.Number:
        if (rawValue === null || rawValue == undefined) {
          return { value: { value: null } }
        }

        return { value: { value: Number(rawValue) } }
      case AttributeType.Boolean:
      case AttributeType.Text:
        return { value: { value: rawValue } }

      case AttributeType.File:
        // AttributeType.File is only reached for new valueGroups (there is no attributeValue
        // to attach an upload to yet), so this returns the nested insert of the relation.
        if (rawValue?.fileList) {
          return {
            data: rawValue?.fileList
              .map((f: any) => {
                // ignore attribute files here . theay shall not be changed from the service UI
                if (f.type === 'attributeFile') {
                  return false
                }

                if (
                  'status' in f &&
                  typeof f.uid === 'string' &&
                  !f.uploadId &&
                  f.status !== 'done'
                ) {
                  throw new Error(
                    t(`events.serviceModules.errors.uploadStillInProgress`, {
                      fileName: f.name,
                    })
                  )
                }

                if (Number.isInteger(Number(f.uid))) {
                  return {
                    name: f.name,
                    attributeValueUploadVersions: {
                      data: [{ fileId: Number(f.uid) }],
                    },
                  }
                }

                if (f?.response?.uid) {
                  return {
                    name: f.name,
                    attributeValueUploadVersions: {
                      data: [{ fileId: Number(f.response.uid) }],
                    },
                  }
                }

                console.warn(
                  `prepareValue(): file is missing uid ${JSON.stringify(
                    rawValue
                  )}`
                )
                return undefined
              })
              .filter(Boolean),
          }
        }
        return { data: [] }
      case AttributeType.Date:
        return { value: prepareDate(rawValue, attribute) }
      // case AttributeType.Select:
      // return { value: rawValue }
      case AttributeType.User:
        return { value: {}, userId: rawValue?.id || null }
      // case AttributeType.Select:
      // return { value: rawValue }
      default:
        console.warn(
          `prepareValue(): No case for attributeType ${attribute.attributeType}`
        )
        return { value: { value: rawValue } }
    }
  }

  const handleNewValueGroups = (
    allValueGroups: FormData[],
    attributeGroup: AttributeGroup
  ): AttributeValueGroups_Insert_Input[] => {
    // creating the objects attribute of the insert call that will include all of the
    // newly created AttributeValueGroups for a single attributeGroupdId
    const objects = allValueGroups
      // removing empty attributeValueGroups
      .filter(valueGroup => valueGroup !== undefined)
      .map<any>(valueGroup => {
        // AttributeValueGroups_Insert_Input
        /*
        /* EdgeCase-Update: looping over attributes instead of valueGroup entries 
        /* uploads do not appear as form values, so no value would be saved for fileUpload fields in repeatable valueGroups when looping 
        /* over valueGroup entries.
        /* using attributes instead will save an empty value for fileUploads, so the field is rendered and can have comments
        */

        // creating value to attribute id relations
        const attributeValues = attributeGroup.attributes.map(attr => {
          const attributeId = Number(attr.id)
          const rawValue = valueGroup[String(attributeId)] || undefined

          // The value of a new attributeValue is always an empty string (better would be null)
          if (
            rawValue === undefined ||
            (Array.isArray(rawValue) &&
              rawValue.every(v =>
                Array.isArray(v)
                  ? v.every(v => v === undefined)
                  : v === undefined
              ))
          ) {
            return {
              attributeId: attributeId,
              value: { value: null },
            }
          }

          const attributeType = attr.attributeType

          if (attributeType === AttributeType.File) {
            return {
              attributeId: attributeId,
              attributeValueUploads: prepareValue(rawValue, attr),
            }
          }

          return {
            attributeId: attributeId,
            ...prepareValue(rawValue, attr),
          }
        })

        return {
          attributeGroupId: attributeGroup.id,
          attributeValues: {
            data: attributeValues,
          },
        }
      })

    return objects
  }

  const isNewAttributeValueGroup = (key: string) =>
    new RegExp(`^${NEW_GROUP_INDICATOR}`).test(key)

  const loading = updateValuesAndCreateValueGroupsLoading
  const error = updateValuesAndCreateValueGroupsError

  return { upload, isNewAttributeValueGroup, loading, error } as const
}
